import { useMutation, useQueryClient } from 'react-query';
import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { getPaymentMethodUseCases } from '@subscriptions/composition';
import type { PaymentSessionMode } from '@subscriptions/domain/types';

/**
 * Variables accepted by the confirm-session mutation.
 *
 * `providerSessionId` is the Stripe session id produced by generate-session;
 * `mode` is the Stripe session mode carried from the checkout route state; and
 * `isUpdate` reflects whether the block already has an attached payment method.
 */
export type ConfirmBlockPaymentSessionVariables = {
  blockUuid: string;
  providerSessionId: string;
  mode: PaymentSessionMode;
  isUpdate: boolean;
};

/**
 * Hook for finalizing a Stripe checkout session for a subscription block (CARD path).
 *
 * Called from the checkout step once Stripe fires `onComplete`. Resolves with the
 * attached {@link PaymentMethodViewDto} the confirmation step renders. The
 * `providerSessionId` is never rendered or logged here (charter §5).
 *
 * The checkout leaves the block changed, so its cached detail and the list are invalidated:
 * otherwise the confirmation's "Ir a mi suscripción" lands on a subscription that still reads
 * as unpaid.
 */
export const useConfirmBlockPaymentSession = () => {
  const queryClient = useQueryClient();
  const { confirmSession } = getPaymentMethodUseCases();

  return useMutation<PaymentMethodViewDto, Error, ConfirmBlockPaymentSessionVariables>(
    ({ blockUuid, providerSessionId, mode, isUpdate }) =>
      confirmSession.execute(blockUuid, providerSessionId, mode, isUpdate),
    {
      onSuccess: (_, { blockUuid }) => {
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'detail', blockUuid]);
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'list']);
      },
    },
  );
};
